import type Vue from 'vue'
import type { Route } from 'vue-router'
import type ICancellationJourney from './ICancellationJourney'
import type { ICustomBanner } from './ICustomBanner'
import type IDocsPreference from './IDocsPreference'
import type IFeatures from './IFeatures'
import type INotification from './INotification'
import type IRenewalDiscount from './IRenewalDiscount'
import type ITrustPilot from './ITrustPilot'
import type { ILinkConfigActionSpecification } from './LinkConfigSpecification'
import type { SummaryPageTooltip } from './SummaryPageTooltip'
import type { IBenefitsDashboardItemSpecification, IDirectDebitPopUpSpecification, IGrantedCoPayMtaPremiumPage, IIgnitePaymentGateway, IPaymentIcons, IPaymentMessagesSpecification, IReferAFriendSpecification, IThreeYearPriceFixOptIn } from '@/configurations/config-objects/ConfigSpecification'
import type IExperiments from '@/configurations/config-objects/IExperiments'
import type Routes from '@/constants/Routes'
import type { PaymentTypeEnum } from '@/services/enums/DLDCommonValuesEnum'
import type ProductEnum from '@/services/enums/ProductEnum'
import type { TierExpressionEnum, TierFeatureType } from '@/services/enums/TierEnum'
import type Policy from '@/services/models/Policy'
import type { TenantConfigName } from '@/services/types/TenantConfigName'
import type ClaimSummary from '@/services/values/ClaimSummary'
import type PolicySummary from '@/services/values/PolicySummary'
import type Step from '@/view-models/Step'
import { TierEnum } from '@/services/enums/TierEnum'

export class LinkConfig {
  name: string
  action: ILinkConfigActionSpecification
  isDisabled?: boolean

  click: (vue?: Vue) => void

  constructor(
    name: string,
    click: (vue?: Vue) => void,
    action: ILinkConfigActionSpecification,
    isDisabled: boolean = false,

  ) {
    this.name = name
    this.click = click
    this.action = action
    this.isDisabled = isDisabled
  }
}

export class TierFeature {
  tier: TierEnum = TierEnum.Tier2
  title: string
  type: TierFeatureType
  expressionKey?: TierExpressionEnum | undefined
  checked?: boolean | undefined
  text?: string
  transactionType?: string

  constructor(tier: TierEnum, title: string, type: TierFeatureType, expressionKey?: TierExpressionEnum, checked?: boolean, transactionType?: string, text?: string) {
    this.tier = tier
    this.title = title
    this.type = type
    this.expressionKey = expressionKey
    this.checked = checked
    this.transactionType = transactionType
    this.text = text
  }
}

interface IConfiguration {
  aggregatorEditRoute: Routes
  alternatePremiumTitleText: string | null
  alternateRenewalPremiumTitleText: string | null
  brochureSite: string
  claimsAndConvictions: boolean
  viewClaimsLinks: (policy: PolicySummary, claim: ClaimSummary) => LinkConfig[]
  alternateChangeDirectDebitPaymentDate: (policy: Policy) => LinkConfig[]
  collapseDocsEndorsementsTables: boolean
  collapseDocsEndorsementsTablesAtDesktop: boolean
  cookiePolicyNotice: string | null
  dashboardClaimsLinks: LinkConfig[]
  defaultPaymentType: PaymentTypeEnum
  cancellationJourney: ICancellationJourney
  docsPreference: (product: ProductEnum) => IDocsPreference
  policyType: (schemeName: string) => string | undefined
  experiments: IExperiments
  features: IFeatures
  footer: string
  footerLinks: LinkConfig[]
  footerLogoPath: string
  goToFirstPageOnPremiumPreviousClick: boolean
  headerText: string
  hideBillingAddress: () => boolean
  hideCoverLevelDetailFeatures: boolean
  hidePaymentCoverSummary: boolean
  showPaymentSummary: boolean
  hidePaymentPagePolicySummary: boolean
  ignitePaymentGateway: IIgnitePaymentGateway | null
  blockMtaIfInArrears: boolean
  aggregatorLandingForProduct: ((product: ProductEnum) => string | null) & ((product: ProductEnum) => string | null)
  actionsForPolicy: ((policy: PolicySummary | Policy, options?: { ignoreUnavailableActions: boolean }) => LinkConfig[]) & ((policy: PolicySummary | Policy, options?: { ignoreUnavailableActions: boolean }) => LinkConfig[])
  claimsLinks: ((policy: PolicySummary) => LinkConfig[]) & ((policy: PolicySummary) => LinkConfig[])
  insureProductAction: (product: ProductEnum) => { label: string, overrideUrl: string | null } | null
  tierFeatures: (product: ProductEnum) => TierFeature[]
  companyNameForProduct: (product: ProductEnum) => string | null
  footerTextForProduct: (product: ProductEnum) => string | null
  footerAlignment: string
  logoUrlForProduct: (product: ProductEnum) => string | null
  headerLogoPathForProduct: (product: ProductEnum) => string | null
  footerLogoPathForProduct: (product: ProductEnum) => string | null
  footerLinksForProduct: (product: ProductEnum) => LinkConfig[] | null
  errorMessageForCode: ((code: number) => string) & ((code: number) => string)
  effectiveFutureDateRestrictionDays: (product: ProductEnum) => number
  directDebitPopUp: ((product: ProductEnum) => IDirectDebitPopUpSpecification) & ((product: ProductEnum) => IDirectDebitPopUpSpecification)
  documentsDeliveryPreferenceModal: { enabled: boolean, text: string }
  mostPopularVap: { enabled: boolean, paymentType: string }
  mtaRenewalDeclineMessage: string
  navLinkForAdditionalProduct: (product: ProductEnum) => string | null
  nbDeclineMessage: string
  notification: INotification
  notificationBannerText: string
  paymentMessages: IPaymentMessagesSpecification
  paymentIcons: IPaymentIcons
  paymentPageNotice: string | null
  paymentSummaryAdditionalProducts: boolean
  directDebitInformation: (schemeName: string, hasDeposit: boolean) => string
  paymentRenewalInformation: string
  policyTakeupNextAddress: string
  rebrokeText: string
  referralText: string
  renewalDateText: string
  renewalDiscount: IRenewalDiscount | null
  renewalEntryPageIndex: number
  renewalOptOutModal: ((product: ProductEnum, policy: Policy) => LinkConfig) & ((product: ProductEnum, policy: Policy) => LinkConfig)
  showCantAfford: boolean
  showCoverLevelDetailOnSchemeCard: boolean
  showPolicyDetailsOnSchemeCard: boolean
  mtaShowAbandonChangeConfirmation: boolean
  showDocumentsPopup: boolean
  showRemoveAll: boolean
  skipAggregatorLandingPage: boolean
  socialLinks: LinkConfig[]
  specialButton: LinkConfig | null
  stepsForProduct: ((product: ProductEnum, route: Route) => Step[]) & ((product: ProductEnum, route: Route) => Step[])
  tenantConfigName: TenantConfigName
  tenantFriendlyName: string
  trustPilot: ITrustPilot | null
  veriskEnabled: boolean
  abiDumpCodeFormat: string
  headerLogoPath: string
  logoUrl: string
  headerAccountText: string
  editAccountText: string
  accountPageText: string
  accountPageAdditionalInformation: string
  installAppText: string
  loginHelpText: string
  mtaVapSelectionInformation: string
  goCardlessRenewalText: string
  goCardlessMonthlyPaymentWithDepositText: string
  goCardlessMonthlyPaymentWithoutDepositText: string
  premiumPageHideNext: boolean
  newBusinessPremiumPageNextClickUpdatesQuote: boolean
  showVapIpt: boolean
  showPremiumOnVapPage: boolean
  accountHeaderActions: LinkConfig[]
  summaryPageTooltips: SummaryPageTooltip[]
  benefitsDashboardItem: IBenefitsDashboardItemSpecification
  alternateAPMTAPremiumHeader: string
  alternateAddNewProductMarkup: string
  authServiceHelpText: string
  readOnlyClaimsPostNewBusiness: boolean
  nonEditableClaimsText: string
  maxVehiclePurchaseDate: number
  showCancellationRetentionPageForProduct: (product: ProductEnum) => boolean | null
  newCustomerPortalForProduct: (product: ProductEnum) => boolean
  newCustomerPortalResumeQuotePage: string
  grantedCoPayOnMtaPremiumPage: IGrantedCoPayMtaPremiumPage
  managePolicyAdditionalInformation: (policy: Policy) => string | null
  manageClaimsEnabled: boolean
  manageClaimsEnabledForProductSection: (product: ProductEnum) => boolean
  goCardlessInstantPaymentEnabled: boolean
  referAFriend: IReferAFriendSpecification
  customHomeBanner: ICustomBanner
  threeYearPriceFixOptIn: IThreeYearPriceFixOptIn
  renewalPremiumPageBanner: ICustomBanner
}

export default IConfiguration
